// Short & clear Searchspring badges

type Badge = { tag?: string; value?: string };
type SearchResponse = { results?: Array<{ badges?: Badge[] }> };
type TagsConfig = Record<
  string,
  { parameters?: { colorText?: string; color?: string } }
>;
type MetaResponse = { badges?: { tags?: TagsConfig } };

const BADGES_SELECTOR = '[data-entity="ss-badges"]';
const PERCENTAGE_OFF_TAG = "percentage-off-sale";
const FETCH_TIMEOUT_MS = 8000;

function fetchJson<T>(url: string): Promise<T | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  return fetch(url, { credentials: "omit", signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));
}

async function fetchBadges(siteId: string, sku: string): Promise<Badge[]> {
  const url =
    "https://api.searchspring.net/api/search/search.json?siteId=" +
    encodeURIComponent(siteId) +
    "&resultsFormat=native&resultsPerPage=1&q=" +
    encodeURIComponent(sku);
  const data = await fetchJson<SearchResponse>(url);
  const first = data?.results && data.results[0];
  return first && Array.isArray(first.badges) ? first.badges : [];
}

async function fetchTagsConfig(siteId: string): Promise<TagsConfig | null> {
  const url =
    "https://" +
    encodeURIComponent(siteId) +
    ".a.searchspring.io/api/meta/meta?siteId=" +
    encodeURIComponent(siteId);
  const data = await fetchJson<MetaResponse>(url);
  return data?.badges && typeof data.badges.tags === "object"
    ? (data.badges.tags ?? null)
    : null;
}

function renderBadges(
  containerElement: HTMLElement,
  badges: Badge[],
  tagsConfig: TagsConfig | null
) {
  const hasPercentageOff = badges.some(
    (badge) => badge?.tag === PERCENTAGE_OFF_TAG
  );
  const fragment = document.createDocumentFragment();

  for (const badge of badges) {
    const tag = badge.tag || "";
    const value = badge.value || "";
    if (!tag && !value) continue;
    if (tag === "sale" && hasPercentageOff) continue;

    const badgeWrapper = document.createElement("div");
    badgeWrapper.className = "card__badge top";

    const badgeLabel = document.createElement("span");
    badgeLabel.className = tagsConfig ? "ss-pdp-badge" : "ss-pdp-badge-default";

    const parameters = tagsConfig?.[tag]?.parameters;
    if (parameters?.colorText) badgeLabel.style.color = parameters.colorText;
    if (parameters?.color) badgeLabel.style.backgroundColor = parameters.color;

    badgeLabel.textContent =
      tag === PERCENTAGE_OFF_TAG && value ? `${value} OFF` : value || tag;
    badgeWrapper.append(badgeLabel);
    fragment.append(badgeWrapper);
  }

  if (!fragment.childNodes.length) return;
  containerElement.classList.add("card__badges");
  if (!tagsConfig) containerElement.classList.add("badges", "badges--top-left");
  containerElement.append(fragment);
}

async function mountBadges(containerElement: HTMLElement) {
  if (
    containerElement.dataset.ssBadgesBound === "true" ||
    containerElement.childElementCount
  )
    return;
  const { id: sku, siteId } = containerElement.dataset;
  if (!sku || !siteId) return;

  containerElement.dataset.ssBadgesBound = "true";
  const [badges, tagsConfig] = await Promise.all([
    fetchBadges(siteId, sku),
    fetchTagsConfig(siteId),
  ]);
  if (badges.length) renderBadges(containerElement, badges, tagsConfig);
}

function mountBadgesIn(rootNode: ParentNode) {
  rootNode.querySelectorAll(BADGES_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) void mountBadges(element);
  });
}

if (document.readyState !== "loading") mountBadgesIn(document);
document.addEventListener("DOMContentLoaded", () => mountBadgesIn(document));

document.addEventListener("shopify:section:load", (event) => {
  const targetElement = event.target;
  if (targetElement instanceof HTMLElement) mountBadgesIn(targetElement);
});

